import { CustomDatePicker } from '@components/controls/CustomDatePicker';
import React, { useEffect, useState } from 'react';
import './ZoneMap.css';
import useGetMacroplanogramResults from 'src/store/hooks/useGetMacroplanogramResults';
import Masthead, { MastheadTitle } from '@amzn/meridian/masthead';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import FileInput from '@amzn/meridian/file-input';
import { useAppSelector } from 'src/store/hooks';
import { selectProgram } from 'src/store/slices/programSlice';
import AgileURLParameterTypes from '@components/agile/types/AgileURLParameterTypes';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Papa from 'papaparse';
import { downloadCSV } from './utils/CsvUtils';
import { MacroData, zoneColors, FixtureComponent, MacroAsinFacingData, AsinFacingData } from './types/zoneMapType';
import NavigationHelper from '@components/agile/utils/URLParametersUtils';
import { parseMacroData, getZoneColor, getConcatnatedSubcategoryName } from './utils/FixtureDataUtils';
import FixtureBox from './agileComponents/FixtureComponent';
import {
  storeToFixtureSetsMap,
  storeToRowNumberMap,
  storeToIncreasingOrderRowMap,
  storeToRowNumberToGroupLabelMap,
} from '../../constants/floorPlan';
import { AgileMacroplanogram } from './types/AgileMacroplanogramTypes';
import StoreSelector from '../selectors/ProgramStoreSelector';

export interface ZoneMapProps {
  urlParams: AgileURLParameterTypes;
  setUrlParameter: (value: AgileURLParameterTypes) => void;
}
const ZoneMap = (props: ZoneMapProps) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { urlParams, setUrlParameter } = props;
  const programType = useAppSelector(selectProgram);
  const [store, setStore] = useState(urlParams.storeId);
  const [date, setDate] = useState(urlParams.date || new Date().toISOString().split('T')[0]);
  const [selectedZoneId] = useState(urlParams.zoneId);
  const [macroData, setMacroData] = useState<MacroData[]>([]);
  const timeStamp = new Date(date).getTime();
  const { macroplanogramList } = useGetMacroplanogramResults({
    storeId: store,
    timestamp: timeStamp,
    includeFuture: false,
    includeDraft: false,
  });
  if (programType == null) {
    throw new Error('ProgramType is not initialized');
  }

  const handleFixtureClick = (zoneName: string, fixtureId: string, zoneId: string) => {
    navigate('/agileManagement', {
      state: {
        storeId: searchParams.get('storeId'),
        date: searchParams.get('date'),
        zoneId,
      } as AgileURLParameterTypes,
    });
  };

  const handleKeyPress = (
    event: React.KeyboardEvent<HTMLDivElement>,
    zoneName: string,
    fixtureId: string,
    entryId: string,
  ) => {
    // Trigger click action on 'Enter' key press
    if (event.key === 'Enter') {
      handleFixtureClick(zoneName, fixtureId, entryId);
    }
  };
  const extractFixtureIds = (macroplangramList: AgileMacroplanogram[] | null | undefined) =>
    new Set(macroplangramList?.flatMap((macroplanogram) => macroplanogram.fixtureIds ?? []) ?? []);

  const parseLdpData = (dataArray: { [key: string]: string }[]) => {
    const agileFixtureLists: Set<string> = extractFixtureIds(macroplanogramList);
    return dataArray
      .map((row) => ({
        asin: row.ASIN,
        facings: parseInt(row['Faceout Quantity'], 10),
        fixtureId: row['Rack ID'],
        storeId: row['Store ID'],
      }))
      .filter(
        (row: AsinFacingData) => agileFixtureLists.has(row.fixtureId) && row.storeId === store && row.asin.length > 0,
      );
  };

  const aggregateDataByZone = (
    ldpData: AsinFacingData[],
    macroplangramList: AgileMacroplanogram[],
  ): MacroAsinFacingData[] =>
    macroplangramList.flatMap((macroplangram) => {
      const asinTotalFacing: { [asin: string]: number } = {};
      ldpData.forEach((asinFacingData) => {
        if (macroplangram.fixtureIds?.includes(asinFacingData.fixtureId)) {
          asinTotalFacing[asinFacingData.asin] = (asinTotalFacing[asinFacingData.asin] ?? 0) + asinFacingData.facings;
        }
      });

      return Object.entries(asinTotalFacing).map(([asin, facings]: [string, number]) => ({
        zoneName: getConcatnatedSubcategoryName(macroplangram),
        fixtureIds: macroplangram.fixtureIds,
        asin,
        facings,
      }));
    });

  const onFileAttached = (files: File[] = []): void => {
    const file: File | undefined = files[0];
    if (file) {
      const reader: FileReader = new FileReader();
      reader.onload = (event: ProgressEvent<FileReader>): void => {
        if (event.target && event.target.result) {
          const csvContent = Papa.parse(event.target.result as string, {
            header: true,
          });
          const dataArray = csvContent.data as { [key: string]: string }[];
          const matchedData = parseLdpData(dataArray);
          const exportData = aggregateDataByZone(matchedData, macroplanogramList ?? []);
          downloadCSV(exportData, `${store}-AsinsByZone`);
        }
      };
      reader.readAsText(file);
    } else {
      console.error('No file attached.');
    }
  };

  useEffect(() => {
    setSearchParams(
      NavigationHelper.createSearchParams(
        {
          storeId: store,
          date,
        },
        urlParams.demo,
      ),
    );
    setUrlParameter({
      storeId: store,
      date,
      zoneId: selectedZoneId,
      demo: urlParams.demo,
    });
  }, [date, store, setSearchParams, setUrlParameter, selectedZoneId, urlParams.demo]);

  useEffect(() => {
    if (!macroplanogramList) {
      setMacroData([]);
    } else {
      const parsedMacroData = parseMacroData(macroplanogramList);
      setMacroData(parsedMacroData);
    }
  }, [macroplanogramList]);

  const rows: Record<number, FixtureComponent[]> = {};

  // hardcode for now, in future we will implement real floorplan
  const fixturesSetList: Set<string>[] = storeToFixtureSetsMap.get(store) ?? [];
  const labelsOnTopOfFixtureRowNumberSet: Set<number> = storeToRowNumberMap.get(store) ?? new Set();
  const storeSet: Set<string> = new Set<string>(storeToFixtureSetsMap.keys());
  const rowNumberToGroupLabelMap: Map<number, string> =
    (storeToRowNumberToGroupLabelMap.get(store) as Map<number, string>) ?? new Map();

  macroData?.forEach(({ zoneName, fixtureIds, zoneId }) => {
    fixtureIds.forEach((fixtureId) => {
      const { row, rowNumber, fixtureType, columnNumber } = fixtureId;
      const fixtureIdLabel = `${row}${rowNumber}.${fixtureType}${columnNumber}`;
      const isOddRow = rowNumber % 2 === 1;

      // if there are fixtures not in fixturesSetList, put it into later row
      let number = fixturesSetList.length;
      if (storeSet.has(store)) {
        for (let i = 0; i <= fixturesSetList.length - 1; ) {
          if (fixturesSetList[i].has(fixtureIdLabel)) {
            number = i;
            break;
          }
          i += 1;
        }
      } else {
        number = rowNumber;
      }
      if (!rows[number]) {
        rows[number] = [];
      }
      const labelOnTopOfFixture =
        (storeSet.has(store) && labelsOnTopOfFixtureRowNumberSet.has(number)) || (!storeSet.has(store) && isOddRow);
      rows[number].push(
        <FixtureBox
          key={fixtureIdLabel}
          zoneName={zoneName}
          fixtureIdLabel={fixtureIdLabel}
          zoneId={zoneId}
          handleFixtureClick={handleFixtureClick}
          handleKeyPress={handleKeyPress}
          zoneColors={zoneColors}
          getZoneColor={getZoneColor}
          labelOnTopOfFixture={labelOnTopOfFixture}
        />,
      );
    });
  });

  // sort key based on rowNumber
  Object.keys(rows)
    .map(Number)
    .sort((a, b) => a - b);

  // sort content based on fixture type and columnNumber
  Object.keys(rows).forEach((rowKey) => {
    const rowNumber = Number(rowKey);
    rows[rowNumber].sort((a, b) => {
      const [, fixtureTypeA, columnStrA] = a.props.fixtureIdLabel?.split('.')[1].match(/([a-zA-Z]+)(\d+)/) || [];
      const [, fixtureTypeB, columnStrB] = b.props.fixtureIdLabel?.split('.')[1].match(/([a-zA-Z]+)(\d+)/) || [];
      const columnNumA = parseInt(columnStrA, 10);
      const columnNumB = parseInt(columnStrB, 10);
      const IncreasedOrderedRowNumberSet = storeToIncreasingOrderRowMap.get(store) ?? new Set();

      // First compare by fixture type
      if (fixtureTypeA !== fixtureTypeB) {
        return fixtureTypeA.localeCompare(fixtureTypeB);
      }
      // hardcode compare order
      if (storeSet.has(store) && IncreasedOrderedRowNumberSet.has(rowNumber)) {
        return columnNumA - columnNumB;
      }
      return columnNumB - columnNumA;
    });
  });

  return (
    <Column width="100%" height="100%">
      <Row width="100%" widths={['100%']}>
        <Masthead size="large" backgroundColor="surface">
          <MastheadTitle>
            <Row>
              <StoreSelector selectedStoreId={store} onStoreSelect={setStore} />
              <CustomDatePicker value={date} onChange={setDate} label="Live Date" />
            </Row>
          </MastheadTitle>
          <FileInput
            accept=".csv"
            uploadButtonLabel="Translate LDP Plan"
            onFileAttached={onFileAttached}
            uploadButtonDisabled={macroData.length === 0}
            data-testid="file-input"
            uploadButtonType="primary"
          />
        </Masthead>
      </Row>
      {macroData?.length > 0 && (
        <>
          <Row width="100%" alignmentHorizontal="center">
            <div>
              {Object.keys(rows).map((rowKey) => (
                <>
                  {rows[Number(rowKey)][0]?.props.labelOnTopOfFixture &&
                    rowNumberToGroupLabelMap.has(Number(rowKey)) && (
                      <div className="group-label">{rowNumberToGroupLabelMap.get(Number(rowKey))}:</div>
                    )}
                  <div key={rowKey} className="row">
                    {rows[Number(rowKey)]}
                  </div>
                </>
              ))}
            </div>
          </Row>
          <Row width="100%" alignmentHorizontal="center">
            <div className="front-of-store">Front of Store</div>
          </Row>
        </>
      )}
    </Column>
  );
};

export default ZoneMap;
